import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import ProgressIndicator from '../profile-completion-wizard-step-1-role-confirmation/components/ProgressIndicator';
import ContractorForm from './components/ContractorForm';
import ClientForm from './components/ClientForm';
import AffiliateForm from './components/AffiliateForm';
import profileService from '../../services/profileService';

const ProfileCompletionWizardStep2 = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    // Redirect if profile already completed
    if (userProfile?.profile_completed) {
      const roleRoutes = {
        superadmin: '/super-admin-dashboard',
        client: '/client-job-management',
        contractor: '/contractor-dashboard',
        affiliate: '/affiliate-dashboard'
      };
      navigate(roleRoutes?.[userProfile?.role] || '/contractor-dashboard');
      return;
    }

    // Load existing profile data if available
    loadExistingProfile();
  }, [userProfile, navigate]);

  const loadExistingProfile = async () => {
    if (!user?.id || !userProfile?.role) return;

    setIsLoading(true);
    try {
      // Load basic profile data
      const basicProfile = await profileService?.getUserProfile(user?.id);
      
      let roleSpecificData = {};
      
      // Load role-specific data
      if (userProfile?.role === 'contractor') {
        const contractorProfile = await profileService?.getContractorProfile(user?.id);
        if (contractorProfile) {
          roleSpecificData = {
            skills: contractorProfile?.skills || [],
            experienceLevel: contractorProfile?.experience_level,
            certifications: contractorProfile?.certifications || [],
            certificationsText: contractorProfile?.certifications?.join(', ') || '',
            hourlyRate: contractorProfile?.hourly_rate,
            availability: contractorProfile?.availability || [],
            travelDistance: contractorProfile?.travel_distance,
            serviceTypes: contractorProfile?.service_types || []
          };
        }
      } else if (userProfile?.role === 'client') {
        const clientProfile = await profileService?.getClientProfile(user?.id);
        if (clientProfile) {
          roleSpecificData = {
            businessType: clientProfile?.business_type,
            businessRegistration: clientProfile?.business_registration,
            billingAddress: clientProfile?.billing_address,
            servicePreferences: clientProfile?.service_preferences || []
          };
        }
      } else if (userProfile?.role === 'affiliate') {
        const affiliateProfile = await profileService?.getAffiliateProfile(user?.id);
        if (affiliateProfile) {
          roleSpecificData = {
            networkType: affiliateProfile?.network_type,
            referralCapabilities: affiliateProfile?.referral_capabilities || [],
            preferredCommissionStructure: affiliateProfile?.preferred_commission_structure,
            commissionRate: affiliateProfile?.commission_rate
          };
        }
      }

      setFormData({
        companyName: basicProfile?.company_name || '',
        phone: basicProfile?.phone || '',
        location: basicProfile?.location || '',
        ...roleSpecificData
      });
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (userProfile?.role === 'contractor') {
      if (!formData?.skills || formData?.skills?.length === 0) {
        newErrors.skills = 'Selecteer minimaal één vaardigheid';
      }
      if (!formData?.experienceLevel) {
        newErrors.experienceLevel = 'Selecteer uw ervaringsniveau';
      }
      if (!formData?.availability || formData?.availability?.length === 0) {
        newErrors.availability = 'Selecteer uw beschikbaarheid';
      }
    } else if (userProfile?.role === 'client') {
      if (!formData?.companyName) {
        newErrors.companyName = 'Bedrijfsnaam is verplicht';
      }
      if (!formData?.businessType) {
        newErrors.businessType = 'Selecteer bedrijfstype';
      }
      if (!formData?.phone) {
        newErrors.phone = 'Telefoonnummer is verplicht';
      }
      if (!formData?.location) {
        newErrors.location = 'Locatie is verplicht';
      }
    } else if (userProfile?.role === 'affiliate') {
      if (!formData?.companyName) {
        newErrors.companyName = 'Bedrijfsnaam is verplicht';
      }
      if (!formData?.networkType) {
        newErrors.networkType = 'Selecteer netwerktype';
      }
      if (!formData?.referralCapabilities || formData?.referralCapabilities?.length === 0) {
        newErrors.referralCapabilities = 'Selecteer minimaal één verwijzingsmogelijkheid';
      }
      if (!formData?.preferredCommissionStructure) {
        newErrors.preferredCommissionStructure = 'Selecteer commissie structuur';
      }
      if (!formData?.phone) {
        newErrors.phone = 'Telefoonnummer is verplicht';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSaveProgress = async () => {
    setIsSaving(true);
    try {
      await saveProfileData(false);
      alert('Voortgang opgeslagen!');
    } catch (error) {
      console.error('Error saving progress:', error);
      alert('Fout bij opslaan: ' + (error?.message || 'Onbekende fout'));
    } finally {
      setIsSaving(false);
    }
  };

  const saveProfileData = async (completed = false) => {
    // Update basic profile
    await profileService?.updateBasicProfile(user?.id, {
      fullName: userProfile?.full_name,
      phone: formData?.phone,
      location: formData?.location,
      companyName: formData?.companyName
    });

    // Save role-specific data
    if (userProfile?.role === 'contractor') {
      await profileService?.upsertContractorProfile(user?.id, {
        skills: formData?.skills,
        experienceLevel: formData?.experienceLevel,
        certifications: formData?.certifications,
        hourlyRate: formData?.hourlyRate,
        availability: formData?.availability,
        travelDistance: formData?.travelDistance,
        serviceTypes: formData?.serviceTypes
      });
    } else if (userProfile?.role === 'client') {
      await profileService?.upsertClientProfile(user?.id, {
        businessType: formData?.businessType,
        businessRegistration: formData?.businessRegistration,
        billingAddress: formData?.billingAddress,
        servicePreferences: formData?.servicePreferences
      });
    } else if (userProfile?.role === 'affiliate') {
      await profileService?.upsertAffiliateProfile(user?.id, {
        companyName: formData?.companyName,
        networkType: formData?.networkType,
        referralCapabilities: formData?.referralCapabilities,
        preferredCommissionStructure: formData?.preferredCommissionStructure,
        commissionRate: formData?.commissionRate
      });
    }

    // Update completion status
    await profileService?.updateProfileCompletion(user?.id, completed ? 3 : 2, completed);
  };

  const handleComplete = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await saveProfileData(false);
      
      // Navigate to step 3 for verification and final review
      navigate('/profile-completion-wizard-step-3-verification-and-dashboard-access');
    } catch (error) {
      console.error('Error completing profile:', error);
      setErrors({ submit: error?.message || 'Fout bij opslaan van profiel' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/profile-completion-wizard-step-1-role-confirmation');
  };

  const getRoleTitle = () => {
    const titles = {
      contractor: 'Vakman Profiel',
      client: 'Opdrachtgever Profiel',
      affiliate: 'Partner Profiel'
    };
    return titles?.[userProfile?.role] || 'Profiel';
  };

  if (isLoading && !formData?.companyName && !formData?.skills) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground">
            {getRoleTitle()}
          </h1>
          <p className="text-lg text-muted-foreground">
            Vul uw professionele informatie in
          </p>
        </div>

        {/* Progress Indicator */}
        <ProgressIndicator
          currentStep={2}
          totalSteps={3}
          estimatedTime="5-10 minuten"
        />

        {/* Form Content */}
        <div 
          className="bg-white border-2 border-slate-200 rounded-lg p-6 md:p-8"
          style={{
            boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
          }}
        >
          {userProfile?.role === 'contractor' && (
            <ContractorForm
              formData={formData}
              errors={errors}
              onChange={handleChange}
            />
          )}
          
          {userProfile?.role === 'client' && (
            <ClientForm
              formData={formData}
              errors={errors}
              onChange={handleChange}
            />
          )}
          
          {userProfile?.role === 'affiliate' && (
            <AffiliateForm
              formData={formData}
              errors={errors}
              onChange={handleChange}
            />
          )}

          {errors?.submit && (
            <div className="mt-6 p-4 bg-destructive/10 border border-destructive rounded-lg">
              <p className="text-sm text-destructive">{errors?.submit}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <Button
            variant="outline"
            size="lg"
            onClick={handleBack}
            iconName="ArrowLeft"
            iconPosition="left"
          >
            Terug
          </Button>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              variant="outline"
              size="lg"
              onClick={handleSaveProgress}
              loading={isSaving}
              iconName="Save"
              iconPosition="left"
            >
              Opslaan
            </Button>
            
            <Button
              variant="default"
              size="lg"
              onClick={handleComplete}
              loading={isLoading}
              iconName="Check"
              iconPosition="right"
            >
              Voltooien
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionWizardStep2;